import { Link, useSearchParams } from 'react-router-dom';
import { ProAdvLogo } from '@/components/brand/proadv-logo';

/**
 * F12 Billing — pagina de retorno do pagamento apos confirmacao. E o
 * `return_url`/`success_url` de dois fluxos:
 *
 * - Checkout redirect (round 4): `${webBase}/checkout/sucesso?session_id=...`
 *   — sempre sucesso (o Stripe so chega aqui apos pagar).
 * - Payment Element (round 6): `${webBase}/checkout/sucesso` como
 *   `confirmParams.return_url` — o Stripe anexa `redirect_status` (e
 *   `payment_intent`/`setup_intent`) na query, podendo ser sucesso,
 *   processamento assincrono ou falha.
 *
 * Em ambos os casos o provisionamento do tenant acontece via webhook,
 * assincrono; o usuario recebe o e-mail de definicao de senha por email,
 * nao por essa tela — os 3 estados abaixo so ajustam a MENSAGEM exibida.
 */

const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const SURFACE = 'rgba(27,16,41,0.85)';

type ResultState = 'success' | 'processing' | 'failed';

/**
 * `redirect_status` so existe no retorno do Payment Element. Ausente
 * (fluxo de checkout redirect, ou acesso direto a URL) e tratado como
 * sucesso — preserva o comportamento original desta pagina.
 */
function resolveState(redirectStatus: string | null): ResultState {
  if (!redirectStatus || redirectStatus === 'succeeded') return 'success';
  if (redirectStatus === 'processing') return 'processing';
  return 'failed';
}

const COPY: Record<
  ResultState,
  { icon: string; accent: string; title: string; body: string }
> = {
  success: {
    icon: '✓',
    accent: 'rgba(74,222,128,0.22)',
    title: 'Pagamento confirmado!',
    body: 'Enviamos um e-mail para você definir sua senha e acessar o VoxisAdv. Confira sua caixa de entrada (e o spam, por garantia).',
  },
  processing: {
    icon: '⏳',
    accent: 'rgba(232,185,106,0.28)',
    title: 'Pagamento em processamento',
    body: 'Seu pagamento está sendo confirmado. Assim que aprovado, enviamos um e-mail para você definir sua senha e acessar o VoxisAdv.',
  },
  failed: {
    icon: '!',
    accent: 'rgba(239,68,68,0.3)',
    title: 'Não foi possível confirmar o pagamento',
    body: 'O pagamento não foi concluído. Você pode tentar novamente com outro método ou cartão.',
  },
};

export default function CheckoutSucessoPage() {
  const [searchParams] = useSearchParams();
  const state = resolveState(searchParams.get('redirect_status'));
  const copy = COPY[state];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0A0710',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX (-50%)',
          width: '60vw',
          height: '40vw',
          maxWidth: 700,
          maxHeight: 500,
          background: `radial-gradient(ellipse at center, ${copy.accent} 0%, transparent 70%)`,
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <div style={{ marginBottom: 40, position: 'relative' }}>
        <ProAdvLogo variant="horizontal" tone="dark" size={32} />
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: SURFACE,
          border: `1px solid ${copy.accent}`,
          borderRadius: 18,
          padding: 'clamp(36px, 6vw, 52px)',
          backdropFilter: 'blur(24px)',
          boxShadow: `0 0 80px ${copy.accent}, 0 2px 48px rgba(0,0,0,0.4)`,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: copy.accent,
            border: `1px solid ${copy.accent}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: 24,
          }}
        >
          {copy.icon}
        </div>
        <h1
          style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontWeight: 400,
            fontSize: 'clamp(24px, 4vw, 28px)',
            color: FG,
            marginBottom: 12,
          }}
        >
          {copy.title}
        </h1>
        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 15,
            color: MUTED,
            lineHeight: 1.6,
            marginBottom: 32,
          }}
        >
          {copy.body}
        </p>
        <Link
          to={state === 'failed' ? '/#planos' : '/sign-in'}
          style={{
            display: 'inline-block',
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            color: '#1A0F00',
            textDecoration: 'none',
            background: `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${CHAMPAGNE_LIGHT} 100%)`,
            borderRadius: 10,
            padding: '13px 28px',
            boxShadow: '0 4px 24px rgba(232,185,106,0.3)',
          }}
        >
          {state === 'failed' ? 'Tentar novamente' : 'Ir para o login'}
        </Link>
      </div>
    </div>
  );
}
